import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { CARD_CLASS, OMS_COLORS, tint } from '../../dashboard/omsDashTheme';
import { ADMIN_EYEBROW_CLASS } from '../adminTheme';
import type { AdminInsight, InsightSeverity } from '../utils/adminMetrics';

const SEVERITY: Record<InsightSeverity, { ink: string; fill: string; label: string }> = {
  critical: { ink: '#9F1239', fill: '#9F1239', label: 'Act today' },
  warning: { ink: '#8A5A08', fill: '#C9A961', label: 'Watch' },
  info: { ink: '#14606B', fill: '#14606B', label: 'Note' },
};

interface Props {
  insights: AdminInsight[];
  loading: boolean;
  onOpen: (insight: AdminInsight) => void;
}

/**
 * Insights feed — rules-based anomalies in plain language; every card
 * deep-links into the drilled, time-scoped view that proves it. This feed
 * is the page's only alarm surface.
 */
export default function AdminInsightsFeed({ insights, loading, onOpen }: Props) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      aria-label="Insights feed"
      className={`${CARD_CLASS} p-4`}
    >
      <p className={ADMIN_EYEBROW_CLASS}>Insights · auto-detected anomalies</p>
      {loading && insights.length === 0 ? (
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
          ))}
        </div>
      ) : insights.length === 0 ? (
        <p className="mt-3 text-sm font-semibold" style={{ color: '#1F5A47' }}>
          Nothing anomalous this window — growth, adoption and task flow are all inside their normal bands.
        </p>
      ) : (
        <ul className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {insights.map((insight, index) => {
            const tone = SEVERITY[insight.severity];
            return (
              <motion.li
                key={insight.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * index, duration: 0.3 }}
                className="rounded-xl border p-3"
                style={{
                  backgroundColor: tint(tone.fill, 0.05),
                  borderColor: insight.severity === 'critical' ? 'rgba(159,18,57,0.35)' : 'rgba(11,59,46,0.18)',
                }}
              >
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em]"
                  style={{ color: tone.ink, backgroundColor: tint(tone.fill, 0.14) }}
                >
                  {tone.label}
                </span>
                <p className="mt-1.5 text-[12px] font-semibold leading-snug" style={{ color: OMS_COLORS.ink }}>
                  {insight.sentence}
                </p>
                <button
                  type="button"
                  onClick={() => onOpen(insight)}
                  className="mt-2 flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] transition hover:bg-[rgba(20,96,107,0.08)]"
                  style={{ color: '#14606B', border: '1px solid rgba(20,96,107,0.3)', backgroundColor: '#fcfaf3' }}
                >
                  Open the proof <ArrowUpRight className="h-3 w-3" />
                </button>
              </motion.li>
            );
          })}
        </ul>
      )}
    </motion.section>
  );
}
